import { Arg, Args, Ctx, FieldResolver, Query, Resolver, Root } from 'type-graphql'

import { User } from '@things-factory/auth-base'
import { Domain, getQueryBuilderFromListParams, getRepository, ListParam } from '@things-factory/shell'

import { Board } from '../board/board.js'
import { PlayGroup } from './play-group.js'
import { PlayGroupList } from './play-group-type.js'

@Resolver(PlayGroup)
export class PlayGroupQuery {
  @Query(returns => PlayGroup, { nullable: true, description: 'Finds a single play group by its ID.' })
  async playGroup(@Arg('id') id: string, @Ctx() context: ResolverContext): Promise<PlayGroup> {
    const { domain } = context.state

    return await getRepository(PlayGroup).findOne({
      where: { domain: { id: domain.id }, id }
    })
  }

  @Query(returns => PlayGroup, { nullable: true, description: 'Finds a single play group by its name.' })
  async playGroupByName(@Arg('name') name: string, @Ctx() context: ResolverContext): Promise<PlayGroup> {
    const { domain } = context.state

    return await getRepository(PlayGroup).findOne({
      where: { domain: { id: domain.id }, name }
    })
  }

  @Query(returns => PlayGroupList, { description: 'Retrieves a paginated list of play groups.' })
  async playGroups(
    @Args(type => ListParam) params: ListParam,
    @Ctx() context: ResolverContext
  ): Promise<PlayGroupList> {
    const { domain } = context.state

    const queryBuilder = getQueryBuilderFromListParams({
      repository: getRepository(PlayGroup),
      params,
      domain,
      searchables: ['name', 'description']
    })

    const [items, total] = await queryBuilder.getManyAndCount()

    return { items, total }
  }

  @FieldResolver(type => [Board], {
    description: 'Resolves the ordered list of boards for a play group, sorted by the custom order array.'
  })
  async boards(@Root() playGroup) {
    const { order = [], boards = [] } = await getRepository(PlayGroup).findOne({
      where: { id: playGroup.id },
      relations: ['boards']
    })

    return boards.sort((a, b) => {
      // 배열 A에 포함된 아이디의 순서를 가져옵니다.
      const indexOfOrder = (order || []).indexOf(a.id)
      const indexOfBoards = (order || []).indexOf(b.id)

      // 두 아이디의 순서를 비교하여 정렬합니다.
      if (indexOfOrder === -1 && indexOfBoards === -1) {
        // 두 아이디 모두 배열 A에 없는 경우, 그대로 유지합니다.
        return 0
      } else if (indexOfOrder === -1) {
        // 아이디 A만 배열 A에 없는 경우, 아이디 B를 먼저 정렬합니다.
        return 1
      } else if (indexOfBoards === -1) {
        // 아이디 B만 배열 A에 없는 경우, 아이디 A를 먼저 정렬합니다.
        return -1
      } else {
        // 두 아이디 모두 배열 A에 있는 경우, 배열 A의 아이디 순서대로 정렬합니다.
        return indexOfOrder - indexOfBoards
      }
    })
  }

  @FieldResolver(type => Domain, { description: 'Resolves the domain associated with the play group.' })
  async domain(@Root() playGroup: PlayGroup) {
    return await getRepository(Domain).findOneBy({ id: playGroup.domainId })
  }

  @FieldResolver(type => User, { description: 'Resolves the user who last updated the play group.' })
  async updater(@Root() playGroup: PlayGroup): Promise<User> {
    return await getRepository(User).findOneBy({ id: playGroup.updaterId })
  }

  @FieldResolver(type => User, { description: 'Resolves the user who created the play group.' })
  async creator(@Root() playGroup: PlayGroup): Promise<User> {
    return await getRepository(User).findOneBy({ id: playGroup.creatorId })
  }
}
